import { c as _c } from "react/compiler-runtime";
import type { ApiNodeDataAuthentication, AuthMethodConfig } from '@onefootprint/request-types/dashboard';
import { Form } from '@onefootprint/ui';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import useOrgSession from '@/hooks/use-org-session';
import { TenantFeatureGate } from '@/types/tenant';
import type { ApplyEditMutation } from '../../../base-node';

// The node stores a config object per method; the checkbox group works with the bare kind, so we
// convert at the form boundaries.
type AuthMethodValue = AuthMethodConfig['kind'];
type AuthenticationFormData = {
  configs: AuthMethodValue[];
};
const AuthenticationForm = t0 => {
  const $ = _c(49);
  const {
    mutation,
    nodeData,
    nodeId,
    canEdit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "authentication"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    toggle
  } = useDirty();
  const featureGates = useOrgSession().data?.featureGates ?? [];
  const hasSmsOtp = featureGates.includes(TenantFeatureGate.SmsOtp);
  const hasEmailOtp = featureGates.includes(TenantFeatureGate.EmailOtp);
  const t2 = nodeData.configs.map(_temp).filter(kind => kind === "phone" ? hasSmsOtp : hasEmailOtp);
  let t3;
  if ($[1] !== t2) {
    t3 = {
      defaultValues: {
        configs: t2
      }
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const methods = useForm(t3);
  const {
    handleSubmit,
    formState: t4
  } = methods;
  const {
    isDirty
  } = t4;
  let t5;
  if ($[3] !== isDirty || $[4] !== toggle) {
    t5 = () => {
      toggle("authentication", isDirty);
    };
    $[3] = isDirty;
    $[4] = toggle;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== isDirty) {
    t6 = [isDirty];
    $[6] = isDirty;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] !== mutation || $[9] !== nodeId) {
    t7 = async data => {
      const configs = data.configs.map(_temp2);
      ;
      try {
        await mutation.mutateAsync({
          nodeId,
          kind: "update_node",
          nodeData: {
            kind: "authentication",
            configs
          }
        });
      } catch (t8) {
        const _ = t8;
      }
    };
    $[8] = mutation;
    $[9] = nodeId;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleFormSubmit = t7;
  let t8;
  if ($[11] !== handleFormSubmit || $[12] !== handleSubmit) {
    t8 = handleSubmit(handleFormSubmit);
    $[11] = handleFormSubmit;
    $[12] = handleSubmit;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t) {
    t9 = t(_temp3);
    $[14] = t;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <span className="text-body-2 text-primary">{t9}</span>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t) {
    t11 = t(_temp4);
    $[18] = t;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = <h3 className="text-label-2 text-primary">{t11}</h3>;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = !canEdit;
  let t14;
  if ($[22] !== hasSmsOtp || $[23] !== t) {
    t14 = hasSmsOtp ? [{
      label: t(_temp5),
      value: "phone",
      hint: t(_temp6)
    }] : [];
    $[22] = hasSmsOtp;
    $[23] = t;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== hasEmailOtp || $[26] !== t) {
    t15 = hasEmailOtp ? [{
      label: t(_temp7),
      value: "email",
      hint: t(_temp8)
    }] : [];
    $[25] = hasEmailOtp;
    $[26] = t;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t14 || $[29] !== t15) {
    t16 = [...t14, ...t15];
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t) {
    t17 = {
      validate: value => value.length > 0 || t(_temp9)
    };
    $[31] = t;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t13 || $[34] !== t16 || $[35] !== t17) {
    t18 = <Form.CheckboxGroup name="configs" disabled={t13} className="flex flex-col gap-3" options={t16} rules={t17} />;
    $[33] = t13;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t12 || $[38] !== t18) {
    t19 = <div className="flex flex-col gap-4">{t12}{t18}</div>;
    $[37] = t12;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t10 || $[41] !== t19) {
    t20 = <div className="px-5 py-4 border rounded bg-primary border-tertiary flex flex-col gap-6">{t10}{t19}</div>;
    $[40] = t10;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t20 || $[44] !== t8) {
    t21 = <form id="authentication-form" onSubmit={t8}>{t20}</form>;
    $[43] = t20;
    $[44] = t8;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== methods || $[47] !== t21) {
    t22 = <FormProvider {...methods}>{t21}</FormProvider>;
    $[46] = methods;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  return t22;
};
export default AuthenticationForm;
function _temp(m) {
  return m.kind;
}
function _temp2(kind_0) {
  return kind_0 === "phone" ? {
    kind: "phone"
  } : {
    kind: "email"
  };
}
function _temp3($) {
  return $.instructions;
}
function _temp4($_0) {
  return $_0.methods.title;
}
function _temp5($_1) {
  return $_1.methods.phone.label;
}
function _temp6($_2) {
  return $_2.methods.phone.hint;
}
function _temp7($_3) {
  return $_3.methods.email.label;
}
function _temp8($_4) {
  return $_4.methods.email.hint;
}
function _temp9($_5) {
  return $_5.methods.required;
}